import { MsgExecuteContract } from "@goblinhunt/cosmes/client"
import { ConfigResponse } from "models/factory/ConfigResponse"
import { StakeResponse, StakingInfo } from "models/staking/StakeResponse"
import { BalanceResponse } from "models/token/BalanceResponse"
import { TokenInfoResponse } from "models/token/TokenInfoResponse"
import { useAsset } from "provider/AssetProvider"
import { useWallet } from "provider/WalletProvider"
import { useState } from "react"
import { CLASSIC_CHAIN_ID } from "utils/Constants"
import { fromDenom, toDenom } from "utils/Helpers"
import { showLoadingToast, showTxToast } from "utils/Toasts"

type Props = {
    factoryConfig?: ConfigResponse,
    feeTokenInfo?: TokenInfoResponse,
    stakingInfo?: StakingInfo,
    feeBalance?: BalanceResponse,
    onUnstakeComplete: () => void
}

export function UnstakeBox({ factoryConfig, feeTokenInfo, stakingInfo, onUnstakeComplete, feeBalance }: Props) {
    const { tokenInfo, stakingAddress } = useAsset()
    const [amount, setAmount] = useState("")
    const { address: sender, post } = useWallet()

    const isValid = () => {
        if (!sender) return "Please connect your wallet first"
        if (!tokenInfo || !stakingAddress) return "Please select an asset to unstake"
        if (!factoryConfig) return "Cannot load fee, please refresh the page"
        if (isNaN(Number(amount)) || Number(amount) <= 0) return "Amount must be more than 0, use . (period) as decimal separator"
        if (!stakingInfo || toDenom(Number(amount), tokenInfo.decimals) > Number(stakingInfo.amount)) return "Cannot unstake more than your staked asset"
        if (!feeTokenInfo || !feeBalance || Number(factoryConfig.config.unstaking_fee) > Number(feeBalance.balance)) return `Your ${feeTokenInfo?.symbol} balance is not enough to pay the fee`

        return undefined;
    }

    const onUnstake = async () => {
        const increaseAllowanceMsg = {
            increase_allowance: {
                spender: stakingAddress,
                amount: factoryConfig!.config.unstaking_fee
            }
        }

        const executeAllowanceMsg = new MsgExecuteContract({
            sender: sender!,
            contract: factoryConfig!.config.token_contract,
            msg: increaseAllowanceMsg,
            funds: []
        })

        const stakeMsg = {
            unstake: {
                amount: toDenom(Number(amount), tokenInfo?.decimals ?? 6).toString()
            }
        }

        const executeMsg = new MsgExecuteContract({
            sender: sender!,
            contract: stakingAddress!,
            msg: stakeMsg,
            funds: []
        })
        const msgs = [executeAllowanceMsg, executeMsg]

        const result = await showLoadingToast("Signing transaction", () => post({
            msgs,
        }))

        await showTxToast(result)
        onUnstakeComplete()
    }


    return <div className="card">
        <h5 className="card-header">Unstake</h5>
        <div className="card-body">
            <div className="mb-3">
                <div className="input-group">
                    <input
                        value={amount}
                        onChange={({ target }) => setAmount(target.value)}
                        type="number"
                        className="form-control"
                        placeholder="Amount"
                    />
                    <span className="input-group-text">{tokenInfo?.symbol}</span>
                </div>
            </div>
            {
                !isValid() && <div className="alert alert-warning" role="alert">You will be charged {fromDenom(Number(factoryConfig!.config.unstaking_fee), feeTokenInfo?.decimals ?? 6)} {feeTokenInfo?.symbol} for unstaking</div>
            }
            {
                isValid() && <div className="alert alert-danger" role="alert">{isValid()}</div>
            }

            <button onClick={onUnstake} disabled={isValid() != undefined} className="btn btn-primary">Unstake</button>
        </div>
    </div>
}